import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Sidebar } from './Sidebar';
import { LayoutProps, SidebarProps } from '@/types/components';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import { storage } from '@/utils/storage';
import { useModalDialog } from '@/hooks/useModalDialog';

/** `storage` key for the desktop rail state (RESPONSIVE_WEB_PLAN decision 2A). */
export const SIDEBAR_COLLAPSED_KEY = 'sidebar_collapsed';

const readCollapsed = (): boolean =>
  storage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';

const HamburgerIcon = () => (
  <svg
    className="w-6 h-6 text-gray-700"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 6h16M4 12h16M4 18h16"
    />
  </svg>
);

const BackIcon = () => (
  <svg
    className="w-6 h-6 text-gray-700"
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    aria-hidden="true"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M15 19l-7-7 7-7"
    />
  </svg>
);

/**
 * App shell. Two independent pieces of navigation state:
 *
 * - `isCollapsed` — desktop/tablet icon rail, persisted in `storage`
 *   (expanded when never set). Toggled only by the rail button.
 * - `isDrawerOpen` — phone drawer (`<dialog>` in the top layer), never
 *   persisted. Toggled only by the hamburger; closes on route change,
 *   Escape, backdrop tap and when the viewport grows past `md`.
 *
 * `currentPath` still feeds the Sidebar's active state; route-change
 * detection uses `useLocation()` so it works even if the prop lags.
 */
export const Layout: React.FC<LayoutProps> = ({
  children,
  currentPath = '/',
  routeMeta,
  renderSidebar,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();

  const [isCollapsed, setIsCollapsed] = useState<boolean>(readCollapsed);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const hamburgerRef = useRef<HTMLButtonElement>(null);

  const toggleCollapsed = useCallback(() => {
    setIsCollapsed((prev) => {
      const next = !prev;
      storage.setItem(SIDEBAR_COLLAPSED_KEY, String(next));
      return next;
    });
  }, []);

  const openDrawer = useCallback(() => setIsDrawerOpen(true), []);
  const closeDrawer = useCallback(() => setIsDrawerOpen(false), []);

  // Route change closes the drawer (effect keyed on the router, not the prop).
  useEffect(() => {
    setIsDrawerOpen(false);
  }, [location.pathname]);

  // Growing past `md` while the drawer is open: the sidebar goes inline.
  useEffect(() => {
    if (!isMobile) setIsDrawerOpen(false);
  }, [isMobile]);

  // showModal()/open fallback, Escape, backdrop tap, scroll lock. Focus goes to
  // the drawer's first link on open and back to the hamburger on close (the
  // hamburger explicitly: a pointer click need not have focused it).
  const { setDialogRef, isNativeModal, dialogProps } = useModalDialog({
    isOpen: isDrawerOpen,
    onRequestClose: closeDrawer,
    getInitialFocus: (dialog) =>
      dialog.querySelector<HTMLElement>('a[href], button:not([disabled])'),
    returnFocusTo: hamburgerRef,
  });

  const sidebarProps: SidebarProps = {
    isCollapsed: isMobile ? false : isCollapsed,
    onToggleCollapse: toggleCollapsed,
    currentPath,
    variant: isMobile ? 'drawer' : 'inline',
    ...(isMobile ? { onRequestClose: closeDrawer } : {}),
  };
  const sidebar = renderSidebar ? (
    renderSidebar(sidebarProps)
  ) : (
    <Sidebar {...sidebarProps} />
  );

  const parentPath = routeMeta?.parentPath;
  const mobileTitle = routeMeta?.mobileTitle;

  return (
    <div className="flex h-screen supports-[height:100dvh]:h-[100dvh] bg-gray-50 overflow-hidden">
      {!isMobile && sidebar}

      {isMobile && isDrawerOpen && (
        <>
          {!isNativeModal && (
            <div
              className="fixed inset-0 z-40 bg-gray-900/50"
              onClick={closeDrawer}
              aria-hidden="true"
            />
          )}
          <dialog
            ref={setDialogRef}
            // Explicit aria-modal: the `open` fallback path is not modal.
            aria-modal="true"
            aria-label="Menu navigasi"
            {...dialogProps}
            className={`p-0 m-0 max-h-none max-w-none h-[100dvh] bg-transparent backdrop:bg-gray-900/50 ${
              isNativeModal ? '' : 'fixed inset-y-0 left-0 z-50'
            }`}
          >
            {/* Animate the wrapper, never the <dialog>: a transformed dialog
                would become the containing block for portaled menus. */}
            <div className="h-full w-[280px] max-w-[85vw] animate-slide-in-left">
              {sidebar}
            </div>
          </dialog>
        </>
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        <header
          data-tap-region
          className="bg-white border-b border-gray-200 flex items-center gap-1 h-14 px-4 md:h-auto md:px-6 md:py-4"
        >
          {isMobile &&
            (parentPath ? (
              <button
                type="button"
                aria-label="Kembali"
                onClick={() => navigate(parentPath)}
                className="h-11 w-11 flex items-center justify-center rounded-lg hover:bg-gray-100 active:bg-gray-100 transition-colors"
              >
                <BackIcon />
              </button>
            ) : (
              <button
                ref={hamburgerRef}
                type="button"
                aria-label="Buka menu"
                aria-haspopup="dialog"
                aria-expanded={isDrawerOpen}
                onClick={isDrawerOpen ? closeDrawer : openDrawer}
                className="h-11 w-11 flex items-center justify-center rounded-lg hover:bg-gray-100 active:bg-gray-100 transition-colors"
              >
                <HamburgerIcon />
              </button>
            ))}

          {mobileTitle && (
            <h1 className="text-[17px] font-sans font-semibold text-gray-900 truncate md:hidden">
              {mobileTitle}
            </h1>
          )}
        </header>

        <main id="main" className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};
